import type { StoryType } from "@config";
import { reactQueryUtils } from "@lib/react-query";
import { useMutation } from "@tanstack/react-query";
import { pagesApi } from "./pages.api";
import { pagesKeys } from "./pages.keys";
import { pageBelongsInList } from "./pages.cache";
import type { SearchPagesParams, TPage, UpdatePageParams } from "./types";

/** A cached list query: its key, and the rows it holds. */
type CachedList = [unknown[], TPage[]];

/**
 * Patches every cached page list, and returns the snapshots to roll back to.
 *
 * The lists are keyed by their params — ["pages", "list", { …filters }] — so
 * this walks the matching queries rather than reading one fixed key. It used to
 * read `["pages", "list"]` exactly, which no query is stored under, so the
 * optimistic update it was written for never actually applied: the list only
 * changed when the refetch landed, seconds later.
 *
 * The updater is given the list's own params, so a row can be dropped from a
 * list it no longer belongs to — a page filed into another folder leaves the
 * folder that was open.
 */
const patchCachedPagesLists = (
  updater: (pages: TPage[], params?: SearchPagesParams) => TPage[],
): CachedList[] => {
  const entries = reactQueryUtils.getQueriesData<TPage[]>([
    ...pagesKeys.lists(),
  ]);

  const snapshots: CachedList[] = [];
  entries.forEach(([key, pages]) => {
    if (!Array.isArray(pages)) return;
    const queryKey = key as unknown[];
    snapshots.push([queryKey, pages]);
    // The params are the last segment of pagesKeys.list(params).
    const last = queryKey[queryKey.length - 1];
    const params =
      last && typeof last === "object"
        ? (last as SearchPagesParams)
        : undefined;
    reactQueryUtils.setQueryData(queryKey, updater(pages, params));
  });
  return snapshots;
};

const restoreCachedPagesLists = (snapshots: CachedList[] | undefined) => {
  snapshots?.forEach(([key, pages]) => {
    reactQueryUtils.setQueryData(key, pages);
  });
};

export const useDeletePageMutation = () => {
  return useMutation({
    mutationFn: (id: string) => pagesApi.deletePage(id),
    onMutate: async (id: string) => {
      await reactQueryUtils.cancelQueries([...pagesKeys.all]);
      const previousPages = patchCachedPagesLists((pages) =>
        pages.filter((page) => page._id !== id),
      );
      return { previousPages };
    },
    onError: (_err, _id, context) => {
      restoreCachedPagesLists(context?.previousPages);
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...pagesKeys.all]);
    },
  });
};

export const useUpdatePageMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdatePageParams;
      /** Read in onMutate and onSettled, not here. */
      skipInvalidation?: boolean;
    }) => pagesApi.updatePage(id, data),
    onMutate: async ({ id, data, skipInvalidation }) => {
      await reactQueryUtils.cancelQueries([...pagesKeys.all]);

      // A caller that opted out of the invalidation gets no optimistic patch
      // either: there would be no refetch to correct it. Those callers also
      // send shapes a shallow merge cannot apply — a whole story document, or
      // Mongo dotted paths like "settings.title", which would land as literal
      // keys and leave the real field untouched and uncorrected.
      if (skipInvalidation)
        return { previousPages: undefined, skipInvalidation };

      const previousPages = patchCachedPagesLists((pages, params) =>
        pages.flatMap((page) => {
          if (page._id !== id) return [page];
          const next = { ...page, ...data } as TPage;
          // A move takes the row out of the list it moved away from, rather
          // than leaving it there until the refetch catches up.
          const movedFolder =
            "folderId" in data && data.folderId !== page.folderId;
          if (movedFolder && !pageBelongsInList(next.folderId, params)) {
            return [];
          }
          return [next];
        }),
      );
      return { previousPages, skipInvalidation };
    },
    onError: (_err, _variables, context) => {
      restoreCachedPagesLists(context?.previousPages);
    },
    onSettled: (_data, _error, variables, context) => {
      if (!variables?.skipInvalidation && !context?.skipInvalidation) {
        reactQueryUtils.invalidateQueries([...pagesKeys.all]);
      }
    },
  });
};

export const useUpdatePageShareTimeMutation = () => {
  return useMutation({
    mutationFn: (id: string) => pagesApi.updatePageShareTime(id),
  });
};

export const useCreatePageMutation = () => {
  return useMutation({
    mutationFn: (story: StoryType) => pagesApi.createPage(story),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...pagesKeys.all]);
    },
  });
};

export const useSaveAndPublishPageMutation = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TPage> }) =>
      pagesApi.saveAndPublishPage(id, data),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...pagesKeys.all]);
    },
  });
};

export const useGetUploadFileTokenMutation = () => {
  return useMutation({
    mutationFn: ({
      filename,
      contentType,
      typeStory,
    }: {
      filename: string;
      contentType: string;
      typeStory: boolean;
    }) => pagesApi.getUploadFileToken(filename, contentType, typeStory),
  });
};
